import * as React from "react";
import { useEffect, useState } from "react";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";

import { ContentCategoryRow } from "../../../common/contentTypes";

/**
 * A short menu of emoji that render reliably in Slack, plus a free-text field
 * for anything else. Editing is explicit — picking from the grid stages a
 * choice, Save writes it — so a mis-click does not immediately change what end
 * users see.
 */
const EMOJI_CHOICES = [
  "⭐",
  "🔥",
  "🎬",
  "🎵",
  "🏰",
  "🐾",
  "🍔",
  "⚽",
  "🏈",
  "🎃",
  "🎄",
  "🎤",
  "🧪",
  "🌍",
  "📺",
  "🎮",
];

interface Props {
  category: ContentCategoryRow;
  isOpen: boolean;
  saving?: boolean;
  onClose: () => void;
  onSave: (emoji: string) => void;
}

export const EmojiModal: React.FC<Props> = ({
  category,
  isOpen,
  saving,
  onClose,
  onSave,
}) => {
  const [emoji, setEmoji] = useState(category.emoji ?? "");

  useEffect(() => {
    setEmoji(category.emoji ?? "");
  }, [category]);

  return (
    <Modal isOpen={isOpen} toggle={onClose}>
      <ModalHeader toggle={onClose}>Emoji for “{category.name}”</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="category-emoji">Emoji</Label>
          <Input
            id="category-emoji"
            className="text-center"
            style={{ fontSize: "1.5rem" }}
            placeholder="Paste or type an emoji"
            value={emoji}
            onChange={(e) => setEmoji(e.target.value)}
          />
          <small className="text-muted">
            Shown next to the category, and in the end-user picker.
          </small>
        </FormGroup>

        <div className="d-flex flex-wrap">
          {EMOJI_CHOICES.map((choice) => (
            <Button
              key={choice}
              color={emoji === choice ? "primary" : "light"}
              outline={emoji !== choice}
              className="mr-2 mb-2"
              style={{ fontSize: "1.25rem", lineHeight: 1 }}
              onClick={() => setEmoji(choice)}
            >
              {choice}
            </Button>
          ))}
        </div>

        <Button color="light" size="sm" onClick={() => setEmoji("")}>
          Clear
        </Button>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={saving}>
          Close
        </Button>
        <Button color="primary" onClick={() => onSave(emoji)} disabled={saving}>
          {saving ? "Saving…" : "Save emoji"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
